import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { useContext, useEffect, useState } from "react";
import { EmailSettings } from "schemas/email";
import { CustomEvent } from "schemas/functions";

import TemplateEditButton from "components/TemplateEditButton";
import OutreachSelect from "components/TemplateScreen/OutreachSelect";
import { MAXIMUM_FOLLOWUP_ALLOWED } from "constants/templates";
import { Template } from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";

interface IProps {
  emailSettingId: number;
  templates: Template[];
  handleClose: (e?: CustomEvent) => void;
  updateEmailSettings: (
    emailSetting: EmailSettings,
    templates: Template[],
  ) => void;
}

type TemplateError = {
  idx: number;
  message: string;
};

export default function EditTemplateDialog({
  emailSettingId,
  templates,
  handleClose,
  updateEmailSettings,
}: IProps) {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { emailSettings, currentOrg } = useContext(OrganizationUserContext);
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const { getToken } = useAuth();
  const {
    templates: allTemplates,
    outreachTemplates,
    followupTemplates,
    setCreateTemplateDialogOpen,
  } = useContext(OutreachTemplatesContext);

  const [templatesError, setTemplatesError] = useState<TemplateError | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [modifiedTemplates, setModifiedTemplates] = useState<Template[]>([]);

  const filteredOutreachTemplates = outreachTemplates?.filter(
    (t) => !t.emailTemplateId,
  );
  const filteredFollowUps = followupTemplates?.filter(
    (t) => !t.emailTemplateId,
  );
  const templateFromEmailSettings = emailSettings?.emailSettingTemplates?.map(
    (template) => template.templateId,
  );
  const selectedFollowups = modifiedTemplates?.slice(
    1,
    modifiedTemplates?.length,
  );

  const handleGenerateTemplate = () => {
    trackEvent("Generate Template Button Clicked", {
      Source: "VA Niche Change",
    });
    setCreateTemplateDialogOpen(true);
  };

  const handleTemplateChange = (e: CustomEvent, index: number) => {
    const { value } = e.target;
    const copy = [...modifiedTemplates];
    const template = allTemplates?.find((x) => x.id === value);
    if (template) {
      copy[index] = template;
      setModifiedTemplates(copy);
    }
  };

  const removeFollowUp = (index: number) => {
    const copy = [...modifiedTemplates];
    copy.splice(index, 1);
    setModifiedTemplates(copy);
  };

  const addFollowUp = () => {
    setModifiedTemplates([...modifiedTemplates, followupTemplates?.[0]]);
  };

  const handleEnter = async () => {
    setLoading(true);

    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/email-settings/${emailSettingId}`,
        "PUT",
        {},
        {
          templateIds: modifiedTemplates
            .map((t) => t.id)
            .filter((id): id is number => id !== undefined),
        },
        true,
      );

      const jsonMessage = await res.json();

      if (!res.ok) {
        if (jsonMessage.field) {
          if (
            jsonMessage.field === "templates" &&
            jsonMessage.templateIdx !== undefined &&
            jsonMessage.templateIdx !== null
          ) {
            setTemplatesError({
              idx: jsonMessage.templateIdx,
              message: jsonMessage.message,
            });
            return;
          }
        }
        setAlert(jsonMessage.message, "error");
      } else {
        updateEmailSettings(jsonMessage.emailSetting, modifiedTemplates);
        handleClose();
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setModifiedTemplates(templates || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog
      open={true}
      onClose={handleClose}
      fullScreen={isMobileScreen}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Update Templates</DialogTitle>
      <DialogContent>
        <Typography paragraph>
          Modify the templates used for this niche:
        </Typography>
        <Grid container sx={{ my: 2 }} columnSpacing={0.5}>
          <Grid item xs={12} sm={3}>
            <Typography sx={{ mt: 0.75 }}>Outreach Template</Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            {modifiedTemplates[0] ? (
              <OutreachSelect
                templates={modifiedTemplates}
                handleChange={handleTemplateChange}
                filteredOutreachTemplates={filteredOutreachTemplates}
                templateFromEmailSettings={templateFromEmailSettings}
              />
            ) : (
              <Button
                variant="contained"
                size="small"
                onClick={handleGenerateTemplate}
                sx={{ height: 25, mt: 0.4, textTransform: "none" }}
                disableElevation
              >
                Generate Template
              </Button>
            )}
            {templatesError && templatesError.idx === 0 && (
              <Grid container>
                <Typography
                  variant="caption"
                  sx={{ fontStyle: "italic", color: "error.main" }}
                >
                  {templatesError.message}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid container sx={{ mb: 2 }} columnSpacing={{ xs: 0, sm: 0.5 }}>
          <Grid item xs={12} sm={3}>
            <Typography sx={{ mt: selectedFollowups.length > 0 ? 0.75 : 0.5 }}>
              Follow-up Templates
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            {selectedFollowups?.map((followup, index) => (
              <Grid container sx={{ mb: 1 }} key={index}>
                <Grid container alignItems="center">
                  <Select
                    onChange={(e) => handleTemplateChange(e, index + 1)}
                    value={followup?.id}
                    size="small"
                  >
                    {filteredFollowUps?.map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        <Box>
                          {template.name}
                          {template.id &&
                            templateFromEmailSettings?.includes(
                              template.id,
                            ) && (
                              <Chip
                                label="Last Used"
                                size="small"
                                sx={{
                                  ml: 1,
                                  fontSize: 10,
                                  backgroundColor: "primary.light",
                                }}
                              />
                            )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>

                  {followup?.id && (
                    <TemplateEditButton
                      sx={{ ml: 1 }}
                      templateId={followup.id}
                    />
                  )}

                  <IconButton
                    sx={{ fontSize: 13, color: grey[500] }}
                    onClick={() => removeFollowUp(index + 1)}
                  >
                    <Box component="i" className="fa-regular fa-trash" />
                  </IconButton>
                </Grid>
                {templatesError && templatesError.idx === index + 1 && (
                  <Grid container>
                    <Typography
                      variant="caption"
                      sx={{ fontStyle: "italic", color: "error.main" }}
                    >
                      {templatesError.message}
                    </Typography>
                  </Grid>
                )}
                <Grid container>
                  <Typography
                    variant="caption"
                    sx={{ fontStyle: "italic", fontSize: 10 }}
                    color="textSecondary"
                  >
                    Sent 4 business days after previous email
                  </Typography>
                </Grid>
              </Grid>
            ))}
            {followupTemplates?.length > 0 &&
              modifiedTemplates.length < MAXIMUM_FOLLOWUP_ALLOWED + 1 && (
                <Grid container alignItems="center">
                  <Grid item xs md>
                    <Button
                      disableElevation
                      onClick={addFollowUp}
                      sx={{
                        fontSize: 12,
                        textTransform: "none",
                        px: 0,
                      }}
                    >
                      + Follow Up
                    </Button>
                  </Grid>
                </Grid>
              )}
            {followupTemplates.length === 0 && (
              <Button
                variant="contained"
                size="small"
                onClick={handleGenerateTemplate}
                sx={{
                  height: 25,
                  mt: 0.4,
                  textTransform: "none",
                }}
                disableElevation
              >
                Generate Follow-up
              </Button>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <LoadingButton loading={loading} onClick={handleEnter}>
          Enter
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
